import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Trophy, Flame, Award, Target, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserStats = () => {
  const { getAuthHeader } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/user/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Failed to load your stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-[#2D2A26] border-2 border-black dark:border-white rounded-xl p-6 hard-shadow-lg">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const badgeIcons = {
    'News Rookie': Trophy,
    'News Addict': Flame,
    'News Master': Award,
    'Share Master': TrendingUp,
    'Viral King': Target
  };

  return (
    <div className="bg-white dark:bg-[#2D2A26] border-2 border-black dark:border-white rounded-xl p-6 hard-shadow-lg" data-testid="user-stats">
      <h3 className="text-2xl font-bold mb-6 dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
        {t('stats.title')}
      </h3>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-4 bg-[#FFE66D] border-2 border-black rounded-lg">
          <div className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
            {stats.points}
          </div>
          <div className="text-sm font-bold uppercase mt-1">{t('stats.points')}</div>
        </div>

        <div className="text-center p-4 bg-[#FF6B6B] text-white border-2 border-black rounded-lg">
          <div className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
            {stats.streak_days}
          </div>
          <div className="text-sm font-bold uppercase mt-1">{t('stats.streak')}</div>
        </div>

        <div className="text-center p-4 bg-[#4ECDC4] border-2 border-black rounded-lg">
          <div className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
            {stats.total_shares || 0}
          </div>
          <div className="text-sm font-bold uppercase mt-1">{t('stats.shares')}</div>
        </div>

        <div className="text-center p-4 bg-[#95E1D3] border-2 border-black rounded-lg">
          <div className="text-3xl font-bold" style={{ fontFamily: 'Merriweather, serif' }}>
            {stats.total_reads || 0}
          </div>
          <div className="text-sm font-bold uppercase mt-1">{t('stats.reads')}</div>
        </div>
      </div>

      {stats.badges && stats.badges.length > 0 && (
        <div>
          <h4 className="text-lg font-bold mb-3 dark:text-white" style={{ fontFamily: 'Merriweather, serif' }}>
            {t('stats.yourBadges')}
          </h4>
          <div className="flex flex-wrap gap-3">
            {stats.badges.map((badge) => {
              const Icon = badgeIcons[badge] || Award;
              return (
                <div
                  key={badge}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#FFE66D] to-[#FF6B6B] border-2 border-black rounded-full"
                  data-testid={`badge-${badge}`}
                >
                  <Icon className="w-5 h-5" strokeWidth={2.5} />
                  <span className="font-bold text-sm">{badge}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 p-4 bg-[#F0F0F0] dark:bg-[#3D3A36] border-2 border-black dark:border-gray-600 rounded-lg">
        <p className="text-sm font-bold dark:text-white">
          {t('stats.keepStreak')}
        </p>
      </div>
    </div>
  );
};

export default UserStats;